import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Table, Spin, Space, Tag, Input, InputRef, Button } from "antd";
import { FileTextOutlined, SearchOutlined } from "@ant-design/icons";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { RoleType } from "../../models/Role";
import { useApplicationContext } from "../../application/ApplicationContext";
import { activeUser } from "../../services/AuthServices";
import {
  getAllEvents,
  deleteEvent,
  getAllEventsInactive,
} from "../../services/EventService";
import { formatDate } from "../../core/Utility";
//import "./EventListPage.scss";
import { EventDto, UpdateEventDto } from "../../models/Event";

import type { ColumnsType, ColumnType } from "antd/es/table";
import { FilterConfirmProps } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import { Toast } from "primereact/toast";
import moment from "moment";
///// REDUX IMPORT
import { useDispatch, useSelector } from "react-redux";
import { setCurrentPage } from "../../store-redux/slices/ChangePageSlice";

const FinishedEventsPage = () => {
  const [activeSpinner, setActiveSpinner] = useState<boolean>(false);
  const [eventList, setEventList] = useState<Event[]>([]);
  const [deleteId, setDeleteId] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const [searchedColumn, setSearchedColumn] = useState<string>("");
  const searchInput = useRef<InputRef>(null);
  const toast = useRef<any>(null);
  const [pageNumber, setPageNumber] = useState<any>(1);
  const [paginationNumber, setPaginationNumber] = useState<any>(30);
  const [selectionType, setSelectionType] = useState<"checkbox" | "radio">(
    "checkbox"
  );

  const [selectedEvents, setSelectedEvents] = useState([]);

  const navigate = useNavigate();

  const { setBreadcrumb } = useApplicationContext();

  ///// IMPLEMENTATION REDUX
  const dispatch = useDispatch();
  const currentPage = useSelector((state: any) => state.changePage.currentPage);
  ///// END IMPLEMENTATION REDUX

  useEffect(() => {
    setBreadcrumb(["Događaji", "Lista događaja"]);
    fetchAllEvents();
  }, []);

  const fetchAllEvents = () => {
    setActiveSpinner(true);
    getAllEventsInactive()
      .then((response: any) => {
        const sortedData = [...response.data.result];

        // Sortirajte niz koristeći Moment.js za poređenje datuma u polju "createdAt"
        sortedData.sort((a, b) => {
          const dateA = moment(a.createdAt, "YYYY-MM-DD");
          const dateB = moment(b.createdAt, "YYYY-MM-DD");

          // Sortirajte opadajuće (najnoviji datum prvo)
          return dateB.diff(dateA);
        });

        setEventList(sortedData);
        setPageNumber(currentPage); /// - SET NUMBER PAGE FROM STORE
        return response.data.result;
      })
      .finally(() => setActiveSpinner(false))
      .catch((err: any) => console.log(err));
  };

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: DataIndex
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText("");
  };

  type DataIndex = keyof EventDto;
  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): ColumnType<EventDto> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Pretraži po imenu`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => {
              handleSearch(selectedKeys as string[], confirm, dataIndex);
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90, border: "none" }}
          >
            Pretraži
          </Button>
          <Button
            onClick={() => {
              clearFilters && handleReset(clearFilters);
              confirm({ closeDropdown: false });
              setSearchText((selectedKeys as string[])[0]);
              close();
              setSearchedColumn("");
            }}
            size="small"
            style={{ width: 90 }}
          >
            Poništi
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#C7363D" : undefined }} />
    ),
    onFilter: (
      value,
      record //@ts-ignore
    ) =>
      (record[dataIndex] ?? "")
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
            color: "#0D4077",
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const columns: ColumnsType<UpdateEventDto | any> = [
    {
      title: "Red. broj",
      width: 100,
      dataIndex: "firstName",
      key: "firstName",
      render: (firstName, text, index) => {
        return (
          <span className="blue-text">
            {(pageNumber - 1) * paginationNumber + index + 1}
          </span>
        );
      },
    },
    {
      title: "Naziv događsaja",
      dataIndex: "name",
      key: "name",
      render: (name, id) => {
        return (
          <Link to={`/edit-event/${id.id}`}>
            <span className="blue-text">{name}</span>
          </Link>
        );
      },
      // sorter: (a, b) => (a.name < b.name ? -1 : 1),
      // ...getColumnSearchProps("name"),
    },
    {
      title: "Mesto događaja",
      dataIndex: "place",
      key: "place",
      render: (place) => <span className="blue-text">{place}</span>,
      sorter: (a, b) => (a.place < b.place ? -1 : 1),
      ...getColumnSearchProps("place"),
    },
    {
      title: "Datum početka",
      dataIndex: "dateFrom",
      key: "dateFrom",
      align: "center",
      // defaultSortOrder: "descend",
      sorter: (a, b) =>
        new Date(a?.dateFrom).getTime() < new Date(b?.dateFrom).getTime()
          ? -1
          : 1,
      render: (dateFrom) => (
        <span className="blue-text">
          {formatDate(new Date(dateFrom as Date))}
        </span>
      ),
    },
    {
      title: "Datum završetka",
      dataIndex: "dateTo",
      key: "dateTo",
      align: "center",
      render: (dateTo) => (
        <span className="blue-text">
          {formatDate(new Date(dateTo as Date))}
        </span>
      ),
    },
    {
      title: "Broj prijavljenih",
      dataIndex: "numberRegistrations",
      key: "numberRegistrations",
      align: "center",
      sorter: (a, b) =>
        a.numberRegistrations < b.numberRegistrations ? -1 : 1,
      render: (numberRegistrations) => (
        <span className="blue-text">{numberRegistrations}</span> //TODO...
      ),
    },
    {
      title: "Broj učestvovanih",
      dataIndex: "numberParticipant",
      key: "numberParticipant",
      align: "center",
      sorter: (a, b) => (a.numberParticipant < b.numberParticipant ? -1 : 1),
      render: (numberParticipant) => (
        <span className="blue-text">{numberParticipant}</span> //TODO...
      ),
    },
    {
      title: "Broj odbijenih",
      dataIndex: "numberRejectedRegistrations",
      key: "numberRejectedRegistrations",
      align: "center",
      sorter: (a, b) =>
        a.numberRejectedRegistrations < b.numberRejectedRegistrations ? -1 : 1,
      render: (numberRejectedRegistrations) => (
        <span className="blue-text">{numberRejectedRegistrations}</span> //TODO...
      ),
    },
    {
      title: "Broj otkazanih",
      dataIndex: "numberDeletedRegistrations",
      key: "numberDeletedRegistrations",
      align: "center",
      sorter: (a, b) =>
        a.numberDeletedRegistrations < b.numberDeletedRegistrations ? -1 : 1,
      render: (numberDeletedRegistrations) => (
        <span className="blue-text">{numberDeletedRegistrations}</span> //TODO...
      ),
    },
    // {
    //   title: "Formular prijave",
    //   dataIndex: "form",
    //   key: "form",
    //   align: "center",
    //   render: () => (
    //     <span className="blue-text">
    //       {"Prijava  "}
    //       <FileTextOutlined />
    //     </span>
    //   ),
    // },
    {
      title: "Tagovi",
      dataIndex: "tags",
      key: "tags",
      align: "center",
      render: (_, { tags }) => (
        <>
          {tags?.map((tag: string, index: number) => {
            const colors = ["orange", "geekblue", "green", "blue", "red"];
            let color = colors[index];
            return (
              <Tag color={color} key={tag}>
                {tag}
              </Tag>
            );
          })}
        </>
      ),
    },
    {
      title: "Opcije",
      key: "action",
      fixed: "right",
      width: 250,
      align: "center",
      render: (_, record) => (
        <Space size="middle">
          {(activeUser?.role.name === RoleType.Admin ||
            activeUser?.role.name === RoleType.Supervizor ||
            activeUser?.role.name === RoleType.Employee) && (
            <span
              className="action-button"
              onClick={() => handleEditButton(record.id as string)}
            >
              Pregled
            </span>
          )}
          <span
            className="action-button"
            onClick={() => handleViewButton(record.id as string)}
          >
            Uporedi
          </span>
        </Space>
      ),
    },
  ];

  const handleEditButton = (id: string) => {
    //navigate(`/edit-event/${id}`);
    navigate(`/events-comparation`, { state: [{ id: id }] });
  };

  const handleViewButton = (id: string) => {
    if (selectedEvents.length <= 1) {
      return toast?.current?.show({
        severity: "error",
        detail: `Morate izabrati bar dva događaja!`,
        life: 3000,
      });
    } else {
      let mapedIdEvents = selectedEvents.map((item: any) => {
        return { id: item.id };
      });
      navigate(`/events-comparation`, { state: mapedIdEvents });
    }
  };

  const rowSelection = {
    onChange: (
      selectedRowKeys: React.Key[],
      selectedRows: EventDto[] | any
    ) => {
      setSelectedEvents(selectedRows);
    },
    getCheckboxProps: (record: EventDto | any) => ({
      disabled: record.name === "Disabled User", // Column configuration not to be checked
      name: record.name,
    }),
  };

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="event-list">
        <Spin spinning={activeSpinner}>
          <div className="table">
            <Table
              sticky={true}
              scroll={{ x: 1500 }}
              columns={columns}
              rowKey={(data) => {
                return data.id;
              }}
              pagination={{
                current: pageNumber,
                onChange(current) {
                  setPageNumber(current);
                  dispatch(setCurrentPage(current)); /// DISPATCH ACTION FROM REDUX
                },
                defaultPageSize: 30,
                showSizeChanger: true,
                pageSizeOptions: ["30", "50", "100"],
              }}
              dataSource={eventList}
              onChange={(pagination, filters, sorter, extra) => {
                setPaginationNumber(pagination.pageSize);
              }}
              className="table-container"
              rowSelection={{
                type: selectionType,
                ...rowSelection,
              }}
            />
          </div>
        </Spin>
      </div>
    </PageLayout>
  );
};

export default FinishedEventsPage;
